import { db } from './server/api';
import { collection, doc, setDoc, getDocs, getDoc, deleteDoc    } from 'firebase/firestore';
import { cursosProgramas } from './programas';

// Datos de programas cargados desde programas.js
const programasData = cursosProgramas;

// Datos de programas con estructura completa
// Si actualizas src/programas.js y vuelves a ejecutar subirTodosProgramas,
// se usarán las nuevas imágenes y la nueva configuración.

/**
 * Sube todos los programas a la colección PROGRAMAS en Firestore
 * Cada programa se crea con: nombre, descripción, pensum, código y cards
 * La posición 0 del array cards es la que se muestra en /programas
 */
export const subirTodosProgramas = async () => {
  try {
    const coleccionProgramas = collection(db, 'PROGRAMAS');
    
    for (const programa of programasData) {
      // Usar el código como ID del documento
      const docRef = doc(coleccionProgramas, programa.codigo);
      
      await setDoc(docRef, {
        nombre: programa.nombre,
        descripcion: programa.descripcion,
        categoria: programa.categoria,
        codigo: programa.codigo,
        pensum: programa.pensum,
        materias_claves: programa.materias_claves || [],
        perfil_egresado: programa.perfil_egresado || '',
        campo_laboral: programa.campo_laboral || '',
        habilidades: programa.habilidades || [],
        requisitos: programa.requisitos || [],
        modalidades: programa.modalidades || [],
        cards: programa.cards,
        cardPrincipal: programa.cards[0], // Card que se muestra en /programas (posición 0)
        // Nuevo campo: costes (array de objetos { definicion, valor })
        costes: programa.costes || [],
        fechaCreacion: new Date(),
        activo: true
      });
      
      console.log(`✅ Programa creado: ${programa.nombre}`);
    }
    
    console.log('✅ Todos los programas han sido cargados exitosamente');
    return true;
  } catch (error) {
    console.error('❌ Error al subir programas:', error);
    throw error;
  }
};

/**
 * Obtiene todos los programas de Firestore
 */
export const obtenerTodosProgramas = async () => {
  try {
    const coleccionProgramas = collection(db, 'PROGRAMAS');
    const snapshot = await getDocs(coleccionProgramas);
    
    const programas = [];
    snapshot.forEach((doc) => {
      programas.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return programas;
  } catch (error) {
    console.error('❌ Error al obtener programas:', error);
    throw error;
  }
};

/**
 * Obtiene un programa específico por código
 */
export const obtenerProgramaPorCodigo = async (codigo) => {
  try {
    const docRef = doc(db, 'PROGRAMAS', codigo);
    const docSnapshot = await getDoc(docRef);
    
    if (docSnapshot.exists()) {
      return {
        id: docSnapshot.id,
        ...docSnapshot.data()
      };
    } else {
      console.log('El programa no existe');
      return null;
    }
  } catch (error) {
    console.error('❌ Error al obtener programa:', error);
    throw error;
  }
};

/**
 * Obtiene un cuadernillo por ID
 */
export const obtenerCuadernilloPorId = async (cuadernilloId) => {
  try {
    const docRef = doc(db, 'CUADERNILLOS', cuadernilloId);
    const docSnapshot = await getDoc(docRef);

    if (docSnapshot.exists()) {
      return {
        id: docSnapshot.id,
        ...docSnapshot.data()
      };
    } else {
      console.log('El cuadernillo no existe');
      return null;
    }
  } catch (error) {
    console.error('❌ Error al obtener cuadernillo:', error);
    throw error;
  }
};

/**
 * Obtiene todos los cuadernillos
 */
export const obtenerCuadernillos = async () => {
  try {
    const coleccionCuadernillos = collection(db, 'CUADERNILLOS');
    const snapshot = await getDocs(coleccionCuadernillos);

    const cuadernillos = [];
    snapshot.forEach((doc) => {
      cuadernillos.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return cuadernillos;
  } catch (error) {
    console.error('❌ Error al obtener cuadernillos:', error);
    throw error;
  }
};

/**
 * Crea un nuevo programa en Firestore
 */
export const crearPrograma = async (programaData) => {
  try {
    const coleccionProgramas = collection(db, 'PROGRAMAS');
    const docRef = doc(coleccionProgramas, programaData.codigo);
    
    const nuevoPrograma = {
      nombre: programaData.nombre,
      descripcion: programaData.descripcion,
      descriptionp: programaData.descriptionp || '',
      categoria: programaData.categoria,
      codigo: programaData.codigo,
      pensum: programaData.pensum,
      materias_claves: programaData.materias_claves || [],
      perfil_egresado: programaData.perfil_egresado || '',
      campo_laboral: programaData.campo_laboral || '',
      habilidades: programaData.habilidades || [],
      requisitos: programaData.requisitos || [],
      modalidades: programaData.modalidades || [],
      cards: programaData.cards || [],
      cardPrincipal: programaData.cardPrincipal || (programaData.cards && programaData.cards.length > 0 ? programaData.cards[0] : ''),
      // Nuevo campo: costes
      costes: programaData.costes || [],
      fechaCreacion: new Date(),
      activo: true
    };
    
    await setDoc(docRef, nuevoPrograma);
    console.log(`✅ Programa creado: ${programaData.nombre}`);
    return { id: programaData.codigo, ...nuevoPrograma };
  } catch (error) {
    console.error('❌ Error al crear programa:', error);
    throw error;
  }
};

/**
 * Actualiza un programa existente
 */
export const actualizarPrograma = async (codigoViejo, programaData) => {
  try {
    // Si el código cambió, eliminar el viejo y crear uno nuevo
    if (codigoViejo !== programaData.codigo) {
      const docRefViejo = doc(db, 'PROGRAMAS', codigoViejo);
      await deleteDoc(docRefViejo);
    }
    
    const docRef = doc(db, 'PROGRAMAS', programaData.codigo);
    const programaActualizado = {
      nombre: programaData.nombre,
      descripcion: programaData.descripcion,
      descriptionp: programaData.descriptionp || '',
      categoria: programaData.categoria,
      codigo: programaData.codigo,
      pensum: programaData.pensum,
      materias_claves: programaData.materias_claves || [],
      perfil_egresado: programaData.perfil_egresado || '',
      campo_laboral: programaData.campo_laboral || '',
      habilidades: programaData.habilidades || [],
      requisitos: programaData.requisitos || [],
      modalidades: programaData.modalidades || [],
      cards: programaData.cards || [],
      cardPrincipal: programaData.cardPrincipal || (programaData.cards && programaData.cards.length > 0 ? programaData.cards[0] : ''),
      // Nuevo campo: costes
      costes: programaData.costes || [],
      fechaActualizacion: new Date(),
      activo: true
    };
    
    await setDoc(docRef, programaActualizado);
    console.log(`✅ Programa actualizado: ${programaData.nombre}`);
    return { id: programaData.codigo, ...programaActualizado };
  } catch (error) {
    console.error('❌ Error al actualizar programa:', error);
    throw error;
  }
};

/**
 * Elimina un programa
 */
export const eliminarPrograma = async (codigo) => {
  try {
    const docRef = doc(db, 'PROGRAMAS', codigo);
    await deleteDoc(docRef);
    console.log(`✅ Programa eliminado: ${codigo}`);
    return true;
  } catch (error) {
    console.error('❌ Error al eliminar programa:', error);
    throw error;
  }
};

/* =====================================================
   SECCIONES - ICFES
===================================================== */

/**
 * Crea una nueva seccion en Firestore
 * @param {string} cuadernilloId - ID del cuadernillo al que pertenece
 * @param {Object} sectionData - { nombre }
 */
export const crearCuadernillo = async (cuadernilloData) => {
  try {
    const coleccionCuadernillos = collection(db, 'CUADERNILLOS');
    const docRef = doc(coleccionCuadernillos);

    const nuevoCuadernillo = {
      id: docRef.id,
      nombre: cuadernilloData.nombre,
      fechaCreacion: new Date(),
      activo: true
    };

    await setDoc(docRef, nuevoCuadernillo);
    console.log(`✅ Cuadernillo creado: ${cuadernilloData.nombre}`);
    return nuevoCuadernillo;
  } catch (error) {
    console.error('❌ Error al crear cuadernillo:', error);
    throw error;
  }
};

export const crearSeccion = async (cuadernilloId, sectionData) => {
  try {
    const coleccionSecciones = collection(db, 'SECCIONES');
    const docRef = doc(coleccionSecciones);

    const nuevaSeccion = {
      id: docRef.id,
      cuadernilloId,
      nombre: sectionData.nombre,
      fechaCreacion: new Date(),
      activo: true
    };

    await setDoc(docRef, nuevaSeccion);
    console.log(`✅ Seccion creada: ${sectionData.nombre}`);
    return nuevaSeccion;
  } catch (error) {
    console.error('❌ Error al crear seccion:', error);
    throw error;
  }
};

/**
 * Obtiene todas las secciones de un cuadernillo
 */
export const obtenerSecciones = async (cuadernilloId) => {
  try {
    const coleccionSecciones = collection(db, 'SECCIONES');
    const snapshot = await getDocs(coleccionSecciones);

    const secciones = [];
    snapshot.forEach((doc) => {
      if (doc.data().cuadernilloId === cuadernilloId) {
        secciones.push({
          id: doc.id,
          ...doc.data()
        });
      }
    });

    return secciones;
  } catch (error) {
    console.error('❌ Error al obtener secciones:', error);
    throw error;
  }
};

/**
 * Obtiene una seccion por ID
 */
export const obtenerSeccionPorId = async (seccionId) => {
  try {
    const docRef = doc(db, 'SECCIONES', seccionId);
    const docSnapshot = await getDoc(docRef);

    if (docSnapshot.exists()) {
      return {
        id: docSnapshot.id,
        ...docSnapshot.data()
      };
    } else {
      console.log('La seccion no existe');
      return null;
    }
  } catch (error) {
    console.error('❌ Error al obtener seccion:', error);
    throw error;
  }
};

/**
 * Actualiza una seccion
 */
export const actualizarSeccion = async (seccionId, sectionData) => {
  try {
    const docRef = doc(db, 'SECCIONES', seccionId);

    const seccionActualizada = {
      nombre: sectionData.nombre,
      fechaActualizacion: new Date()
    };

    await setDoc(docRef, seccionActualizada, { merge: true });
    console.log(`✅ Seccion actualizada: ${sectionData.nombre}`);
    return { id: seccionId, ...seccionActualizada };
  } catch (error) {
    console.error('❌ Error al actualizar seccion:', error);
    throw error;
  }
};

/**
 * Elimina una seccion
 */
export const eliminarSeccion = async (seccionId) => {
  try {
    const docRef = doc(db, 'SECCIONES', seccionId);
    await deleteDoc(docRef);
    console.log(`✅ Seccion eliminada: ${seccionId}`);
    return true;
  } catch (error) {
    console.error('❌ Error al eliminar seccion:', error);
    throw error;
  }
};

/* =====================================================
   PREGUNTAS - ICFES
===================================================== */

/**
 * Crea una nueva pregunta en Firestore
 * @param {Object} preguntaData - { sectionId, texto, opciones: {a, b, c, d}, correcta, imageUrl? }
 * imageUrl es opcional - algunas preguntas pueden tener imagen, otras no
 */
const normalizarValorPersistente = (valor) => {
  if (valor === undefined || valor === null) {
    return '';
  }

  return typeof valor === 'string' ? valor : String(valor);
};

const normalizarOpcionesPersistentes = (opciones = {}) => ({
  a: normalizarValorPersistente(opciones.a),
  b: normalizarValorPersistente(opciones.b),
  c: normalizarValorPersistente(opciones.c),
  d: normalizarValorPersistente(opciones.d),
});

export const crearPregunta = async (preguntaData) => {
  try {
    const coleccionPreguntas = collection(db, 'PREGUNTAS');
    const docRef = doc(coleccionPreguntas);

    const nuevaPregunta = {
      id: docRef.id,
      sectionId: preguntaData.sectionId,
      texto: preguntaData.texto,
      opciones: normalizarOpcionesPersistentes(preguntaData.opciones),
      correcta: normalizarValorPersistente(preguntaData.correcta),
      type: preguntaData.type || 'question',
      contextId: preguntaData.contextId || null,
      contextType: preguntaData.contextType || null,
      contextText: normalizarValorPersistente(preguntaData.contextText),
      reference: normalizarValorPersistente(preguntaData.reference),
      imageHeader: normalizarValorPersistente(preguntaData.imageHeader),
      imageUrl: normalizarValorPersistente(preguntaData.imageUrl),
      url: normalizarValorPersistente(preguntaData.url),
      fechaCreacion: new Date(),
      activo: true
    };

    await setDoc(docRef, nuevaPregunta);
    console.log(`✅ Pregunta creada en seccion ${preguntaData.sectionId}`);
    return nuevaPregunta;
  } catch (error) {
    console.error('❌ Error al crear pregunta:', error);
    throw error;
  }
};

/**
 * Obtiene todas las preguntas de una seccion
 */
export const obtenerPreguntasPorSeccion = async (sectionId) => {
  try {
    const coleccionPreguntas = collection(db, 'PREGUNTAS');
    const snapshot = await getDocs(coleccionPreguntas);

    const preguntas = [];
    snapshot.forEach((doc) => {
      if (doc.data().sectionId === sectionId) {
        preguntas.push({
          id: doc.id,
          ...doc.data()
        });
      }
    });

    return preguntas;
  } catch (error) {
    console.error('❌ Error al obtener preguntas:', error);
    throw error;
  }
};

/**
 * Obtiene una pregunta por ID
 */
export const obtenerPreguntaPorId = async (preguntaId) => {
  try {
    const docRef = doc(db, 'PREGUNTAS', preguntaId);
    const docSnapshot = await getDoc(docRef);

    if (docSnapshot.exists()) {
      return {
        id: docSnapshot.id,
        ...docSnapshot.data()
      };
    } else {
      console.log('La pregunta no existe');
      return null;
    }
  } catch (error) {
    console.error('❌ Error al obtener pregunta:', error);
    throw error;
  }
};

/**
 * Actualiza una pregunta (incluyendo imageUrl)
 */
export const actualizarPregunta = async (preguntaId, preguntaData) => {
  try {
    const docRef = doc(db, 'PREGUNTAS', preguntaId);

    const preguntaActualizada = {
      texto: preguntaData.texto,
      opciones: normalizarOpcionesPersistentes(preguntaData.opciones),
      correcta: normalizarValorPersistente(preguntaData.correcta),
      type: preguntaData.type || 'question',
      contextId: preguntaData.contextId || null,
      contextType: preguntaData.contextType || null,
      contextText: normalizarValorPersistente(preguntaData.contextText),
      reference: normalizarValorPersistente(preguntaData.reference),
      imageHeader: normalizarValorPersistente(preguntaData.imageHeader),
      imageUrl: normalizarValorPersistente(preguntaData.imageUrl),
      url: normalizarValorPersistente(preguntaData.url),
      fechaActualizacion: new Date()
    };

    await setDoc(docRef, preguntaActualizada, { merge: true });
    console.log(`✅ Pregunta actualizada: ${preguntaId}`);
    return { id: preguntaId, ...preguntaActualizada };
  } catch (error) {
    console.error('❌ Error al actualizar pregunta:', error);
    throw error;
  }
};

/**
 * Elimina una pregunta
 */
export const eliminarPregunta = async (preguntaId) => {
  try {
    const docRef = doc(db, 'PREGUNTAS', preguntaId);
    await deleteDoc(docRef);
    console.log(`✅ Pregunta eliminada: ${preguntaId}`);
    return true;
  } catch (error) {
    console.error('❌ Error al eliminar pregunta:', error);
    throw error;
  }
};

/**
 * Obtiene todas las preguntas de un cuadernillo (a traves de sus secciones)
 */
export const obtenerTodasPreguntasPorCuadernillo = async (cuadernilloId) => {
  try {
    const secciones = await obtenerSecciones(cuadernilloId);
    const todasLasPreguntas = [];

    for (const seccion of secciones) {
      const preguntas = await obtenerPreguntasPorSeccion(seccion.id);
      todasLasPreguntas.push(...preguntas);
    }

    return todasLasPreguntas;
  } catch (error) {
    console.error('❌ Error al obtener todas las preguntas:', error);
    throw error;
  }
};
